import { ReactElement } from 'react';

import { formatPeriod, IYearMonth } from '~/utils/date/date';
import { formatMinutesAsHoursAndMinutes } from '~/utils/date/duration';
import { ICeMonthlyRecord } from '~/services/api/communityEngagement/types';
import { ReactComponent as DocumentListIcon } from '~/ui/assets/images/documentList.svg';

import { formatDollars } from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/formatAmount';
import getBarTone from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/getBarTone';
import hasThresholds from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/hasThresholds';
import ProgressRow from '../ProgressRow';
import ceStyles from '~/ui/pages/Clients/reusable/CommunityEngagement/CommunityEngagement.module.scss';

import styles from './MonthlyProgress.module.scss';

interface IMonthlyProgressProps {
  selected: IYearMonth;
  record: ICeMonthlyRecord;
}

const MonthlyProgress = ({ selected, record }: IMonthlyProgressProps): ReactElement => {
  const { monthlyMinutesThreshold, monthlyCurrencyThreshold, isTargetMet } = record;

  if (!hasThresholds(record)) {
    return (
      <div className={styles.notConfigured}>
        <DocumentListIcon className={styles.notConfiguredIcon} />
        <p className={styles.notConfiguredText}>
          CE thresholds haven&apos;t been configured for this clinic yet.
        </p>
      </div>
    );
  }

  return (
    <div className={ceStyles.card}>
      <div className={ceStyles.cardHeader}>
        <h4 className={ceStyles.cardTitle}>Monthly Progress</h4>
        <span className={styles.period}>{formatPeriod(selected)}</span>
      </div>
      <div className={styles.progressRows}>
        {!!monthlyMinutesThreshold && (
          <ProgressRow
            label="Hours"
            total={record.totalMinutesWorked}
            threshold={monthlyMinutesThreshold}
            tone={getBarTone({ threshold: monthlyMinutesThreshold, isTargetMet })}
            format={formatMinutesAsHoursAndMinutes}
          />
        )}
        {!!monthlyCurrencyThreshold && (
          <ProgressRow
            label="Dollars"
            total={record.totalCurrencyEarned}
            threshold={monthlyCurrencyThreshold}
            tone={getBarTone({ threshold: monthlyCurrencyThreshold, isTargetMet })}
            format={formatDollars}
          />
        )}
      </div>
    </div>
  );
};

export default MonthlyProgress;
